import { faChevronDown, faChevronUp, faLocationArrow, faRoute } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import GlobalStoreContext from "../../context/GlobalStoreContext";
import Helper from "../../Helper";
import { Dropoff } from "../../models/Dropoff";
import { GateToDropoffRoute } from "../../models/GateToDropoffRoute";
import { HaulRoute } from "../../models/HaulRoute";
import WaypointGuidance from "../DamnPage/WaypointGuidance/WaypointGuidance";
import WaypointStepView from "./WaypointStep";

interface Props {
    route: HaulRoute | GateToDropoffRoute;
    dropoff?: Dropoff;
    title: React.ReactNode;
    // Stacks the steps toggle under the title instead of beside it, for titles that need the full row width.
    toggleBelowTitle?: boolean;
    defaultExpanded?: boolean;
}

const WaypointDirections: React.FC<Props> = (props) => {
    const context = React.useContext(GlobalStoreContext);
    const [expanded, setExpanded] = React.useState<boolean>(props.defaultExpanded ?? false);
    // waypointGuidance is one app-wide flag, so this tracks whether it was started from this route - otherwise every
    // route's directions on the page would open its own guidance overlay at once.
    const [guidingThisRoute, setGuidingThisRoute] = React.useState<boolean>(false);

    const waypointSteps = props.route.waypointDirection?.waypointSteps
        ?.filter((step) => !step._destroy && !step.hidden)
        .sort((a, b) => a.stepNumber - b.stepNumber);

    const startWaypointGuidance = (e: React.MouseEvent) => {
        e.stopPropagation();
        // If we already have a user location, toggle the geolocation control first so that
        // any existing tracking session is reset before starting waypoint guidance.
        if (context.userLocation) {
            context.geoControlRef.current?.trigger();
        }
        setGuidingThisRoute(true);
        context.setWaypointGuidance(true);
    };

    const stepCount = waypointSteps?.length ?? 0;

    // The title always renders, so a route without steps still shows its gate/dropoff. Directions collapse into a
    // compact toggle on the right rather than a full-width label, so this row lines up with rows that have no route.
    return (
        <>
            <div className="flex flex-col">
                <div
                    className={Helper.classNames(
                        "flex",
                        props.toggleBelowTitle ? "flex-col items-start gap-2" : "items-center gap-3"
                    )}
                >
                    <div className={props.toggleBelowTitle ? "w-full" : "flex-1 min-w-0"}>{props.title}</div>

                    {stepCount > 0 && (
                        <button
                            type="button"
                            onClick={() => setExpanded((prev) => !prev)}
                            aria-expanded={expanded}
                            aria-label={`Turn-by-turn directions, ${stepCount} ${stepCount === 1 ? "step" : "steps"}`}
                            title="Turn-by-turn directions"
                            className="shrink-0 flex items-center gap-1.5 whitespace-nowrap text-xs font-medium text-gray-500 dark:text-plot-darkmode-white/70 hover:text-plot-slate dark:hover:text-plot-darkmode-white cursor-pointer transition-colors"
                        >
                            <FontAwesomeIcon icon={faRoute} />
                            {stepCount} {stepCount === 1 ? "step" : "steps"}
                            <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} className="text-[10px]" />
                        </button>
                    )}
                </div>

                {stepCount > 0 && (
                    <div
                        className={Helper.classNames(
                            "overflow-hidden transition-all duration-300 ease-in-out",
                            expanded ? "max-h-62.5 opacity-100 overflow-y-auto mt-3" : "max-h-0 opacity-0"
                        )}
                    >
                        <button
                            onClick={startWaypointGuidance}
                            className="cursor-pointer flex items-center justify-center gap-2 w-full text-center font-semibold bg-plot-slate text-plot-white mb-2 p-2 rounded-lg hover:scale-[1.02] hover:opacity-90 transition-all duration-300"
                        >
                            <FontAwesomeIcon icon={faLocationArrow} />
                            Start Turn-By-Turn Guidance
                        </button>
                        {waypointSteps?.map((step, index) => (
                            <WaypointStepView
                                key={step.id}
                                waypointStep={step}
                                index={index}
                                isLast={index === stepCount - 1}
                            />
                        ))}
                    </div>
                )}
            </div>

            <WaypointGuidance
                active={context.waypointGuidance && guidingThisRoute}
                cancel={() => {
                    context.geoControlRef.current?.trigger();
                    setGuidingThisRoute(false);
                    context.setWaypointGuidance(false);
                }}
                route={props.route}
                dropoff={props.dropoff}
            />
        </>
    );
};

export default WaypointDirections;
